import React, { useEffect, useRef, useState }  from 'react'
import { View, StyleSheet } from 'react-native'
import { Icon, Text, CheckBox, Button, useTheme } from '@ui-kitten/components'
import { useTranslation } from 'react-i18next'
import OperationsList from '../../components/OperationsList'
import HeaderIconButton from '../../components/HeaderIconButton'
import { useAtomValue } from 'jotai'
import { currencyFormatterAtom, userRoleAtom, usersDictAtom } from '../../global/AtomsProvider'
import { renderSalesItemFieldsToShow } from '../SalesHistory/SalesHistoryScreen'
// ATL-1143: `item.status` is the Firestore map for a Firestore read and, on the Supabase read
// path, either that map or a BARE ID — `{item.status?.text}` rendered blank for the latter and
// for every row whose status the old backend transform rebuilt without a `text` key.
import { statusDisplayText, complexSaleStatuses } from '../../utils/complexSaleStatus'

export default function ComplexSalesScreen({ navigation }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const [showActive, setShowActive] = useState(true)
  const [includeSecondQuery, setIncludeSecondQuery] = useState(false)
  const operationsListRef = useRef()
  const userRole = useAtomValue(userRoleAtom)
  const currencyFormatter = useAtomValue(currencyFormatterAtom)
  const usersDict = useAtomValue(usersDictAtom)
  // Built once per render rather than per row: `renderItemFieldsToShow` runs for every item.
  const localizedStatuses = complexSaleStatuses(t)

  useEffect(() => {
    if (navigation.getState()?.index === 0) {
      navigation.setOptions({
        headerLeft: () => (
          <HeaderIconButton
            onPress={() => {
              navigation.navigate('Home')
            }}
            iconName='arrow-back-outline'
          />
        ),
      })
    }
  }, [])
  
  const renderItemFieldsToShow = (item, date) => {
    // If the item is from the sales collection, render it differently
    if (item.isFromSecondCollection) {
      return renderSalesItemFieldsToShow(item, date)
    }
    
    // Original rendering for complex sales items
    return (
      <>
        <View style={styles.dateTimeView}>
          <Icon name='file-text-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {item.name}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='credit-card-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {currencyFormatter.format(item.finalPrice)}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='person-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {usersDict[item.createdBy]?.name}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='heart' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {item.client?.displayName}
          </Text>
        </View>

        <View style={styles.dateTimeView}>
          <Icon name='activity-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {statusDisplayText(item.status, localizedStatuses)}
          </Text>
        </View>
        
        
        <View style={styles.dateTimeView}>
          <Icon name='clock-outline' width={16} height={16} fill={theme['text-basic-color']} style={styles.icon} />
          <Text
            style={styles.itemTitle}
            category='s2'>
            {date}
          </Text>
        </View>
  
      </>
    )
  }

  /**
   * ATL-986: `complexSales` has no entry in OperationsList's collection->endpoint map,
   * so this screen reads Firestore even for organizations cut over to Supabase.
   * `api: null` states that explicitly. If the endpoint is ever wired up without an
   * `isActive` query param, the Supabase read path will refuse to render this list
   * rather than quietly showing inactive complex sales too.
   */
  const activeFilter = {
    firestore: (unsubscribe) => unsubscribe.where('isActive', '==', true),
    api: null,
  }

  const refreshWithFilters = (range, showActiveOnly, includeSalesQuery) => {
    const filters = showActiveOnly ? activeFilter : null
    // `includeSalesQuery` reaches OperationsList through the `secondQuery` PROP (it
    // refetches on change); refresh() only accepts (range, filter), so passing a
    // third argument here did nothing.
    void includeSalesQuery
    operationsListRef.current.refresh(range, filters)
  }

  const additionalDataValidation = (data) => {
    let allDatesSales = 0
    let allDatesQuantity = 0
    const employeesTotals = {}

    for (let item of data) {
      let totalSales = 0
      let totalQuantity = 0

      for (let operation of item.data) {
        const employeeId = operation.isFromSecondCollection ? operation.cashierUID : operation.createdBy
        const employeeName = usersDict?.[employeeId]?.name || employeeId || 'Unknown'
        const amount = operation.isFromSecondCollection
          ? (operation.finalTotalCartPrice ?? operation.finalPrice ?? 0)
          : (operation.finalPrice ?? 0)

        totalSales += Number(amount) || 0
        totalQuantity += 1

        if (!employeesTotals[employeeName]) {
          employeesTotals[employeeName] = {
            complex: { totalSales: 0, totalQuantity: 0 },
            regular: { totalSales: 0, totalQuantity: 0 },
          }
        }

        const groupKey = operation.isFromSecondCollection ? 'regular' : 'complex'
        employeesTotals[employeeName][groupKey].totalSales += Number(amount) || 0
        employeesTotals[employeeName][groupKey].totalQuantity += 1
      }

      console.log(totalSales, totalQuantity)

      allDatesSales += totalSales
      allDatesQuantity += totalQuantity
    }

    console.log(allDatesQuantity, allDatesSales)

    Object.entries(employeesTotals).forEach(([name, totals]) => {
      if (totals.complex.totalQuantity > 0) {
        console.log(name, 'complex', totals.complex.totalSales, totals.complex.totalQuantity)
      }
      if (totals.regular.totalQuantity > 0) {
        console.log(name, 'regular', totals.regular.totalSales, totals.regular.totalQuantity)
      }
    })
  }

  const topView = () => {
    return (
      <>
        <View style={{flexDirection: 'row', alignSelf: 'flex-end'}}>
          { (userRole?.permissions?.['editComplexSaleTemplates'] || userRole?.id === 'admin') && (
            <Button  appearance='ghost' size='small' onPress={() => {
              navigation.navigate('ComplexSaleTemplates')}}
              accessoryLeft={(props) => (<Icon name='edit-outline' {...props} style={{...props.style, marginRight: 0}} />)}
            >
              {t('complexSales.templates')}
            </Button>
          )}
        </View>
        
        <CheckBox style={styles.dateFilter} checked={showActive} onChange={(value) => {
          setShowActive(value)
          if (value)
            refreshWithFilters('allTime', true, false)
          else
            refreshWithFilters({startDate: new Date(), endDate: new Date()}, false, includeSecondQuery)
          }}
        >
          {t('complexSales.showActive')}
        </CheckBox>
        
        <CheckBox style={[styles.dateFilter, {marginTop: 10}]} checked={includeSecondQuery} onChange={(value) => {
          setIncludeSecondQuery(value)
          refreshWithFilters(null, showActive, value)
        }}>
          {t('complexSales.includeSales')}
        </CheckBox>
      </>
    )
  }

  return (
    <View style={styles.container}>
      <OperationsList 
        collectionName='complexSales' 
        dateField={'createdDate'} 
        additionalFilters={showActive ? activeFilter : null} 
        detailScreenName={'ComplexSaleDetail'} 
        topView={topView} 
        initialRange={'allTime'}
        showTotalSum={false} 
        totalSumFormatter={null} 
        totalSumField={null} 
        ref={operationsListRef}
        showAddButton={true} 
        additionalDataValidation={additionalDataValidation} 
        detailScreenParams={{}}
        renderItemFieldsToShow={renderItemFieldsToShow} 
        navigation={navigation} 
        showStoreFilter={true}
        secondQuery={includeSecondQuery ? {
          collectionName: 'sales',
          detailScreenName: 'SaleDetail',
          dateField: 'saleDate'
        } : null}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  dateFilter: {
    marginHorizontal: 20,
  },
  icon: {
    marginRight: 4,
  },
  itemTitle: {
    marginRight: 25,
  },
  dateTimeView: {
    flexDirection: 'row',
    alignItems: 'center'
  },
});
